import React, { useState } from "react";
import ReactDOM from "react-dom";
import { TextInput } from "../../../../components/inputs/TextInput/TextInput";
import { InputAutoComplete } from "../../../../components/InputAutoComplete/InputAutoComplete";
import PhoneInput from "../../../../components/inputs/PhoneInput/PhoneInput";
import ButtonSecondary from "../../../../components/Buttons/ButtonSecondary/ButtonSecondary";
import api from "../../../../utils/api";
import unmask from "../../../../utils/unmask";
import { toast } from "react-toastify";
import axios from "axios";
import "./CompanyProfileModal.css";

const CompanyProfileModal = ({ company, updateUser, onSuccess, onCancel }) => {
  const [saveLoading, setSaveLoading] = useState(false);
  const [brazilStates, setBrazilStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [cepLoading, setCepLoading] = useState(false);
  const [phoneCountryCode, setPhoneCountryCode] = useState("55");

  const [formData, setFormData] = useState({
    userName: company?.name || "",
    companyName: company?.companyName || "",
    legalName: company?.legalName || "",
    cnpj: company?.cnpj || "",
    email: company?.email || "",
    phone: company?.phone || "",
    state: company?.address?.state || "",
    city: company?.address?.city || "",
    zipCode: company?.address?.zipCode || "",
    address: company?.address?.address || "",
    number: company?.address?.number || "",
    neighborhood: company?.address?.neighborhood || "",
    complement: company?.address?.complement || "",
  });

  React.useEffect(() => {
    const fetchStates = async () => {
      try {
        const { data } = await axios.get(
          "https://servicodados.ibge.gov.br/api/v1/localidades/estados"
        );
        setBrazilStates(data);
      } catch (error) {
        console.error("Erro ao carregar estados:", error);
      }
    };
    fetchStates();
  }, []);

  React.useEffect(() => {
    if (formData.state && brazilStates.length > 0) {
      const stateFound = brazilStates.find(state => state.sigla === formData.state);
      if (stateFound) {
        getCities(formData.state);
      }
    }
  }, [formData.state, brazilStates]);

  const getCities = async (name) => {
    try {
      const stateFound = brazilStates.find(state => state.sigla === name);
      if (!stateFound) return;
      const { data } = await axios.get(
        `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${stateFound.id}/municipios`
      );
      if (data) setCities(data);
    } catch (error) {
      console.error("Erro ao carregar cidades:", error);
    }
  };

  const fetchCepData = async (cleanCep) => {
    setCepLoading(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
      const data = await response.json();
      if (!data.erro) {
        setFormData(prev => ({
          ...prev,
          address: data.logradouro,
          neighborhood: data.bairro,
          city: data.localidade,
          state: data.uf
        }));
        toast.success("Endereço preenchido automaticamente!");
      } else {
        toast.error("CEP não encontrado");
      }
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      toast.error("Erro ao buscar CEP");
    } finally {
      setCepLoading(false);
    }
  };

  const handleCepBlur = async (cep) => {
    const cleanCep = cep.replace(/\D/g, '');
    if (cleanCep.length === 8) {
      await fetchCepData(cleanCep);
    }
  };

  const handleCepButtonClick = async () => {
    const cleanCep = formData.zipCode.replace(/\D/g, '');
    if (cleanCep.length === 8) {
      await fetchCepData(cleanCep);
    } else {
      toast.error("CEP deve ter 8 dígitos");
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.userName.trim()) {
      toast.error("Nome é obrigatório");
      return;
    }
    if (!formData.companyName.trim()) {
      toast.error("Nome da empresa é obrigatório");
      return;
    }
    if (!formData.legalName.trim()) {
      toast.error("Razão social é obrigatória");
      return;
    }
    if (!formData.cnpj || formData.cnpj.length < 18) {
      toast.error("CNPJ inválido");
      return;
    }
    if (!formData.email || !formData.email.includes('@')) {
      toast.error("Email inválido");
      return;
    }
    if (!formData.phone.trim()) {
      toast.error("Telefone é obrigatório");
      return;
    }
    if (!formData.zipCode || formData.zipCode.length < 8) {
      toast.error("CEP é obrigatório");
      return;
    }
    if (!formData.address.trim()) {
      toast.error("Endereço é obrigatório");
      return;
    }
    if (!formData.number.trim()) {
      toast.error("Número é obrigatório");
      return;
    }
    if (!formData.neighborhood.trim()) {
      toast.error("Bairro é obrigatório");
      return;
    }
    if (!formData.state) {
      toast.error("Estado é obrigatório");
      return;
    }
    if (!formData.city) {
      toast.error("Cidade é obrigatória");
      return;
    }

    setSaveLoading(true);
    try {
      const { data } = await api.put(`/companies/companies/${company.id}`, {
        data: {
          attributes: {
            name: formData.userName.trim(),
            companyName: formData.companyName.trim(),
            legalName: formData.legalName.trim(),
            companyPhone: `+${phoneCountryCode}${unmask(formData.phone.trim())}`,
            email: formData.email,
            address: {
              state: formData.state,
              city: formData.city,
              zipCode: formData.zipCode,
              address: formData.address,
              number: formData.number,
              neighborhood: formData.neighborhood,
              complement: formData.complement,
              country: "BR"
            },
            cnpj: formData.cnpj,
          },
        },
      });

      if (data) {
        updateUser(data);
        toast.success("Informações atualizadas com sucesso!");
        onSuccess();
      }
    } catch (error) {
      console.log(error);
      toast.error("Ocorreu um erro ao tentar atualizar as informações.");
    } finally {
      setSaveLoading(false);
    }
  };

  const modalContent = (
    <div className="companyProfileModal">
      <div className="companyProfileModalOverlay" onClick={onCancel}></div>

      <div className="companyProfileModalContent">
        <div className="modalHeader">
          <button className="closeButton" onClick={onCancel}>
            ×
          </button>
        </div>

        <div className="companyProfileModalBody">
          <div className="companyProfileFormContainer">
            <div className="planDescription" style={{ fontWeight: 500, fontSize: 20 }}>
              Complete seu cadastro
            </div>
            <p style={{ margin: 0, fontSize: 14 }}>
              Para <strong>contratar um plano</strong>, complete seu cadastro.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <TextInput
                label="Seu nome"
                value={formData.userName}
                onChange={(val) => setFormData({ ...formData, userName: val })}
                placeholder="Seu nome"
              />

              <TextInput
                label="Nome da empresa"
                value={formData.companyName}
                onChange={(val) => setFormData({ ...formData, companyName: val })}
                placeholder="Nome da empresa"
              />

              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ flex: 1 }}>
                  <TextInput
                    label="Razão Social"
                    value={formData.legalName}
                    onChange={(val) => setFormData({ ...formData, legalName: val })}
                    placeholder="Razão Social"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <TextInput
                    label="CNPJ"
                    value={formData.cnpj}
                    onChange={(val) => setFormData({ ...formData, cnpj: val })}
                    placeholder="CNPJ"
                    maskType="cnpj"
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ flex: 1 }}>
                  <PhoneInput
                    label="Telefone"
                    value={formData.phone}
                    onChange={(val) => setFormData({ ...formData, phone: val })}
                    onCountryChange={(code) => setPhoneCountryCode(code)}
                    placeholder="Telefone"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <TextInput
                    label="Email"
                    value={formData.email}
                    onChange={(val) => setFormData({ ...formData, email: val })}
                    placeholder="Email"
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', alignItems: 'end' }}>
                <div style={{ flex: 1 }}>
                  <TextInput
                    label="CEP"
                    value={formData.zipCode}
                    onChange={(val) => setFormData({ ...formData, zipCode: val })}
                    onBlur={() => handleCepBlur(formData.zipCode)}
                    placeholder="CEP"
                    maskType="cep"
                  />
                </div>
                <div style={{ marginTop: '20px' }}>
                  <ButtonSecondary
                    text={cepLoading ? 'Consultando...' : 'Consultar CEP'}
                    onClick={handleCepButtonClick}
                    disabled={cepLoading || !formData.zipCode}
                    loading={cepLoading}
                    fontSize='14px'
                  />
                </div>
              </div>

              <TextInput
                label="Endereço"
                value={formData.address}
                onChange={(val) => setFormData({ ...formData, address: val })}
                placeholder="Endereço"
              />

              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ flex: 1 }}>
                  <TextInput
                    label="Número"
                    value={formData.number}
                    onChange={(val) => setFormData({ ...formData, number: val })}
                    placeholder="Número"
                  />
                </div>
                <div style={{ flex: 2 }}>
                  <TextInput
                    label="Complemento"
                    value={formData.complement}
                    onChange={(val) => setFormData({ ...formData, complement: val })}
                    placeholder="Complemento (opcional)"
                  />
                </div>
              </div>

              <TextInput
                label="Bairro"
                value={formData.neighborhood}
                onChange={(val) => setFormData({ ...formData, neighborhood: val })}
                placeholder="Bairro"
              />

              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ flex: 1 }}>
                  <InputAutoComplete
                    label="Estado"
                    placeholder="Selecione seu estado"
                    options={brazilStates || []}
                    value={brazilStates?.find((state) => state.sigla === formData.state) || null}
                    getOptionLabel={(option) => option.nome || ""}
                    onChange={(event, newValue) => {
                      const selectedValue = newValue?.sigla?.trim() || "";
                      setFormData({ ...formData, state: selectedValue, city: "" });
                      if (selectedValue) getCities(selectedValue);
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <InputAutoComplete
                    label="Cidade"
                    placeholder="Digite sua cidade"
                    options={cities || []}
                    value={cities?.find((city) => city.nome === formData.city) || null}
                    getOptionLabel={(option) => option.nome || ""}
                    onChange={(event, newValue) => {
                      const selectedValue = newValue?.nome?.trim() || "";
                      setFormData({ ...formData, city: selectedValue });
                    }}
                    disabled={!formData.state}
                  />
                </div>
              </div>
            </div>

            <div className="companyProfileModalActions">
              <div className="buttonCancelModal" onClick={onCancel}>
                Cancelar
              </div>
              <div
                className={`buttonSavePaymentMethod ${saveLoading ? 'loading' : ''}`}
                onClick={saveLoading ? undefined : handleSubmit}
              >
                {saveLoading ? 'Salvando...' : 'Salvar e Continuar'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default CompanyProfileModal;

